/**
 * Reactive user settings singleton: { planId, theme }.
 *
 * The cached theme in localStorage ('viisi-mode') is applied on module load so
 * the first paint is right; the real value arrives from GET /api/settings.
 * Every mutation PATCHes the API and mirrors the response into state.
 */

import { apiFetch } from './api';
import type { Settings, Theme } from './types';

const MODE_KEY = 'viisi-mode';

function readCachedMode(): Theme | null {
    try {
        const m = localStorage.getItem(MODE_KEY);
        return m === 'light' || m === 'dark' ? m : null;
    } catch {
        return null;
    }
}

function applyMode(mode: Theme): void {
    if (typeof document !== 'undefined') {
        document.documentElement.dataset.mode = mode;
    }
    try { localStorage.setItem(MODE_KEY, mode); } catch { /* private mode */ }
}

const state = $state<{
    planId: string | null;
    theme: Theme;
    loaded: boolean;
    loading: boolean;
    error: string | null;
}>({
    planId: null,
    theme: readCachedMode() ?? 'dark',
    loaded: false,
    loading: false,
    error: null,
});

// First paint: use whatever the browser remembers.
applyMode(state.theme);

let inflight: Promise<Settings | null> | null = null;

async function load(force = false): Promise<Settings | null> {
    if (state.loaded && !force) return { planId: state.planId ?? '', theme: state.theme };
    if (inflight) return inflight;
    state.loading = true;
    state.error = null;
    inflight = (async () => {
        try {
            const s = await apiFetch<Settings>('/api/settings');
            state.planId = s.planId;
            state.theme = s.theme;
            state.loaded = true;
            applyMode(s.theme);
            return s;
        } catch (err) {
            state.error = err instanceof Error ? err.message : 'Asetusten haku epäonnistui';
            return null;
        } finally {
            state.loading = false;
            inflight = null;
        }
    })();
    return inflight;
}

/** Switch dark/light. Applies immediately, then persists. */
async function setTheme(mode: Theme): Promise<void> {
    const previous = state.theme;
    state.theme = mode;
    applyMode(mode);
    try {
        const s = await apiFetch<Settings>('/api/settings', { method: 'PATCH', body: { theme: mode } });
        state.theme = s.theme;
        state.planId = s.planId;
        applyMode(s.theme);
    } catch (err) {
        state.theme = previous;
        applyMode(previous);
        throw err;
    }
}

/**
 * Switch the active plan. The API moves today's log to the new plan and
 * clears its selections; the caller reloads today if it is showing it.
 */
async function setPlan(planId: string): Promise<Settings> {
    const s = await apiFetch<Settings>('/api/settings', { method: 'PATCH', body: { planId } });
    state.planId = s.planId;
    state.theme = s.theme;
    return s;
}

export const settings = {
    get planId(): string | null { return state.planId; },
    get theme(): Theme { return state.theme; },
    get loaded(): boolean { return state.loaded; },
    get loading(): boolean { return state.loading; },
    get error(): string | null { return state.error; },
    load,
    setTheme,
    setPlan,
};
